import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * Reliably scrolls the provided elements (and window) to the top on every
 * pathname change. Runs three times — immediately, on next animation frame,
 * and after ~220ms — to defeat AnimatePresence exit transitions and
 * Suspense fallback swaps that can grow the page after the first reset.
 *
 * Hash navigation is ignored here (ScrollToTop handles in-page anchors).
 */
export function useScrollResetOnRouteChange(
  getScrollers: () => Array<HTMLElement | null | undefined>
) {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (typeof window !== "undefined" && "scrollRestoration" in window.history) {
      try {
        window.history.scrollRestoration = "manual";
      } catch {
        /* noop */
      }
    }
  }, []);

  useEffect(() => {
    if (hash) return;

    const reset = () => {
      const scrollers = getScrollers();
      for (const el of scrollers) {
        if (el && typeof el.scrollTo === "function") {
          el.scrollTo({ top: 0, left: 0, behavior: "auto" });
        }
      }
      if (typeof window !== "undefined") {
        window.scrollTo({ top: 0, left: 0, behavior: "auto" });
      }
    };

    reset();
    const raf = requestAnimationFrame(reset);
    const t = window.setTimeout(reset, 220);

    return () => {
      cancelAnimationFrame(raf);
      window.clearTimeout(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, hash]);
}
